import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { DocumentService } from '../../shared/services/document.service';
import { DocumentTypes } from '../../../list/document-types';
import { Choice } from '../../../models/choice';
import { DocumentTypeEnum } from '../../../enum/document-type.enum';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatProgressBar } from '@angular/material/progress-bar';
import { finalize, switchMap, takeLast, tap } from 'rxjs/operators';

@Component({
    selector: 'app-shipment-document-upload-modal',
    templateUrl: './shipment-document-upload-modal.component.html',
    styleUrls: ['./shipment-document-upload-modal.component.scss'],
    imports: [ MatDialogTitle, MatDialogContent, MatDialogActions, MatButton, MatFormField, MatLabel, MatSelect, MatOption, MatProgressBar ]
})
export class ShipmentDocumentUploadModalComponent {

    documentTypes: Choice[] = DocumentTypes;
    file: File = null;
    type: DocumentTypeEnum = null;
    uploading = false;
    uploadProgress = 0;
    error = '';

    readonly acceptedExtensions = [ '.pdf', '.png', '.jpg', '.jpeg' ];
    readonly accept = this.acceptedExtensions.join(',');
    readonly allowedMimeTypes = [ 'application/pdf', 'image/png', 'image/jpeg' ];

    constructor(@Inject(MAT_DIALOG_DATA) public data: { shipmentId: string, file?: File },
                private dialogRef: MatDialogRef<ShipmentDocumentUploadModalComponent>,
                private documentService: DocumentService) {
        if (data.file) {
            this.setFile(data.file);
        }
    }

    selectFile(event: Event): void {
        const target = event.target as HTMLInputElement;
        const file = target.files?.item(0);
        if (!file) {
            return;
        }
        this.setFile(file);
    }

    dragOver(event: DragEvent): void {
        event.preventDefault();
    }

    drop(event: DragEvent): void {
        event.preventDefault();
        const file = event.dataTransfer?.files?.item(0);
        if (!file) {
            return;
        }
        this.setFile(file);
    }

    canUpload(): boolean {
        return !!this.file && !!this.type && !this.uploading;
    }

    upload(): void {
        if (!this.canUpload()) {
            return;
        }
        this.uploading = true;
        this.uploadProgress = 0;
        this.error = '';
        this.documentService.createShipmentUpload(this.data.shipmentId, this.file).pipe(
            switchMap(document => this.documentService.uploadFileToUrl(document.url, this.file).pipe(
                tap(progress => this.uploadProgress = progress),
                takeLast(1),
                switchMap(() => this.documentService.setDocumentType(document.id, this.type))
            )),
            finalize(() => this.uploading = false)
        ).subscribe({
            next: () => this.dialogRef.close({ success: true }),
            error: (error) => this.error = this.formatError(error)
        });
    }

    cancel(): void {
        if (this.uploading) {
            return;
        }
        this.dialogRef.close({ success: false });
    }

    setFile(file: File): void {
        this.error = '';
        if (!this.validFile(file)) {
            this.file = null;
            this.error = $localize`Please select a PDF, PNG, JPG, or JPEG file.`;
            return;
        }
        this.file = file;
    }

    validFile(file: File): boolean {
        if (this.allowedMimeTypes.indexOf(file.type) >= 0) {
            return true;
        }
        const lower = file.name.toLowerCase();
        return this.acceptedExtensions.some(ext => lower.endsWith(ext));
    }

    formatError(error: any): string {
        if (typeof error?.error === 'string' && error.error.trim().length > 0) {
            return error.error;
        }
        if (Array.isArray(error?.error?.errors) && error.error.errors.length > 0) {
            return error.error.errors.map(e => e.message || e).join(' ');
        }
        if (error?.message) {
            return error.message;
        }
        return $localize`Upload failed. Please try again.`;
    }
}
